/**
 * Fallback live workout room — used ONLY for freestyle "Go Live" sessions where
 * no specific workout is linked. For workout-linked sessions, the preflight page
 * sends users to the workout page where the real timer + sync live (the floating
 * PiP follows them via LiveVideoContext).
 *
 * This page now just renders an inline video stage and the LiveKit ControlBar.
 * The global FloatingLiveVideo PiP is auto-hidden in favour of the inline stage
 * by suppressing it when this page is active (handled via context flag).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Track } from 'livekit-client';
import { ArrowLeft, Loader2, PhoneOff, Mic, MicOff, VideoOff, Video as VideoIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLiveVideo } from '../context/LiveVideoContext';
import { toast } from 'sonner';

function findCameraPub(participant) {
  if (!participant) return null;
  const pubs = participant.trackPublications;
  if (!pubs) return null;
  for (const pub of pubs.values()) {
    if (pub.source === Track.Source.Camera || pub.kind === Track.Kind.Video) return pub;
  }
  return null;
}

export default function LiveWorkoutRoomPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user } = useAuth();
  const live = useLiveVideo();
  const remoteRef = useRef(null);
  const selfRef = useRef(null);
  const [, force] = useState(0);
  const bump = () => force((n) => n + 1);

  // Auto-start the live session if we landed here directly (e.g. deep link)
  useEffect(() => {
    if (!live.session && sessionId && !live.connected) {
      live.startSession(sessionId).then((res) => {
        if (!res?.ok && !res?.alreadyActive) {
          // Bail to previous page on failure
          navigate(-1);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Attach remote camera
  useEffect(() => {
    if (!live.room) return undefined;
    const remotes = Array.from(live.room.remoteParticipants.values());
    const pub = findCameraPub(remotes[0]);
    if (pub?.track && remoteRef.current) {
      try { pub.track.attach(remoteRef.current); } catch { /* noop */ }
      return () => { try { pub.track.detach(remoteRef.current); } catch { /* noop */ } };
    }
    return undefined;
  });

  // Attach local camera
  useEffect(() => {
    if (!live.room || !selfRef.current) return undefined;
    const pub = findCameraPub(live.room.localParticipant);
    if (pub?.track) {
      try { pub.track.attach(selfRef.current); } catch { /* noop */ }
      return () => { try { pub.track.detach(selfRef.current); } catch { /* noop */ } };
    }
    return undefined;
  });

  // Re-render on participant track changes
  useEffect(() => {
    if (!live.room) return undefined;
    const r = live.room;
    const cb = () => bump();
    r.on('trackSubscribed', cb);
    r.on('trackUnsubscribed', cb);
    r.on('localTrackPublished', cb);
    r.on('localTrackUnpublished', cb);
    r.on('participantConnected', cb);
    r.on('participantDisconnected', cb);
    return () => {
      r.off('trackSubscribed', cb);
      r.off('trackUnsubscribed', cb);
      r.off('localTrackPublished', cb);
      r.off('localTrackUnpublished', cb);
      r.off('participantConnected', cb);
      r.off('participantDisconnected', cb);
    };
  }, [live.room]);

  if (!live.session && !live.connected) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center" data-testid="live-loading">
          <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground">Connecting you to the live room...</p>
        </div>
      </div>
    );
  }

  const room = live.room;
  const remotes = room ? Array.from(room.remoteParticipants.values()) : [];
  const remoteName = remotes[0]?.name || remotes[0]?.identity || 'Your buddy';
  const hasRemoteVideo = !!findCameraPub(remotes[0])?.track;
  const camOff = live.isCamOff();
  const micMuted = live.isMicMuted();

  const handleLeave = async () => {
    await live.endSession();
    toast.info('Left the live workout');
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="live-workout-room">
      <header className="border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-white"
            data-testid="live-back-btn"
            aria-label="back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${live.connected ? 'bg-red-500 animate-pulse' : 'bg-muted-foreground'}`} />
            <h1 className="font-heading text-base sm:text-xl uppercase tracking-wider">Live Workout</h1>
          </div>
        </div>
        <span className="text-xs text-muted-foreground truncate max-w-[40%]">{user?.name || 'You'}</span>
      </header>

      <div className="relative flex-1 min-h-0 bg-zinc-950">
        {hasRemoteVideo ? (
          <video ref={remoteRef} autoPlay playsInline className="w-full h-full object-contain" data-testid="remote-video" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" data-testid="remote-waiting">
            <div className="text-center px-6">
              <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
              <p className="font-heading uppercase tracking-wider text-white text-lg mb-1">Waiting for {remoteName}</p>
              <p className="text-muted-foreground text-sm">We&apos;ll show their video as soon as they join.</p>
            </div>
          </div>
        )}

        {/* Self preview */}
        <div className="absolute top-4 right-4 z-10 w-32 h-44 sm:w-40 sm:h-56 rounded-lg overflow-hidden border-2 border-white/20 bg-zinc-900" data-testid="self-preview">
          {!camOff ? (
            <video ref={selfRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <VideoOff className="h-6 w-6" />
            </div>
          )}
          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] uppercase tracking-wider">You</div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-zinc-950 border-t border-border px-4 py-3 flex items-center justify-center gap-3">
        <Button onClick={live.toggleMic} variant="outline" size="icon" data-testid="ctrl-mic" aria-label="toggle mic">
          {micMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        <Button onClick={live.toggleCam} variant="outline" size="icon" data-testid="ctrl-cam" aria-label="toggle camera">
          {camOff ? <VideoOff className="h-4 w-4" /> : <VideoIcon className="h-4 w-4" />}
        </Button>
        <Button onClick={handleLeave} variant="destructive" data-testid="ctrl-leave">
          <PhoneOff className="h-4 w-4 mr-2" /> Leave
        </Button>
      </div>
    </div>
  );
}
